import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import type { PlayedCriterion } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';

/**
 * « Le nom d'un critère ouvre sa fiche avec sa direction figée » (§12.3).
 *
 * ## The FROZEN direction, and why that is the whole point
 *
 * The card reads `cascade.criteria` - the version the session played - and never
 * the library of today: « c'est elle qui explique le score, et elle peut
 * différer de ce que dit le critère aujourd'hui ». A card that fetched the
 * criterion would explain a score with a direction written after it, which is
 * the one mistake this screen exists to make impossible.
 *
 * So there is no request here at all, which is the second half of the same
 * decision: the reading is already on the session's own answer. The card of the
 * protocol editor (`criterion-requirement-row`) mounts a query because it reads
 * the LIVE object; this one has nothing to ask.
 *
 * ## The card is IN FLOW, where the editor's is a popover
 *
 * It sits in the objectives grid as a full-width row and pushes what follows
 * down, rather than floating over it. The fil is a card with `overflow-hidden` -
 * that is what keeps a step band from squaring off its rounded corners - and an
 * absolutely positioned panel inside it is a panel clipped at the edge, which on
 * the last turn of a conversation is the whole panel. A disclosure that moves
 * the page is the honest form here.
 */

export interface CriterionNameProps {
  criterion: PlayedCriterion;
  open: boolean;
  onToggle: () => void;
  /** A mark the objective block carries, so a test can aim at one name. */
  testId?: string;
}

export function CriterionName({ criterion, open, onToggle, testId }: CriterionNameProps) {
  return (
    <button
      type="button"
      aria-expanded={open}
      data-testid={testId}
      onClick={onToggle}
      className="inline-flex cursor-pointer items-center gap-1 text-left font-semibold text-bleu-nuit hover:underline hover:underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {criterion.content.name}
      <ChevronDown aria-hidden="true" className={`size-3 shrink-0 ${open ? 'rotate-180' : ''}`} />
    </button>
  );
}

export interface CriterionCardProps {
  criterion: PlayedCriterion;
  className?: string;
}

export function CriterionCard({ criterion, className }: CriterionCardProps) {
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.criterionCard}
      data-criterion-id={criterion.id}
      className={`flex flex-col gap-1.5 rounded-md border border-gray-200 bg-white p-3 ${className ?? ''}`}
    >
      <p className="flex flex-wrap items-baseline gap-2">
        <span className="font-semibold text-noir text-sm">{criterion.content.name}</span>
        <span className="rounded-pill bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 text-xs">
          v{criterion.versionNumber} jouée
        </span>
      </p>
      {/*
        `whitespace-pre-wrap`: a direction is written in paragraphs by the person
        who wrote it, and collapsing them would reshape the sentence a score is
        being explained by.
      */}
      <p className="whitespace-pre-wrap break-words text-gray-700 text-sm">
        {criterion.content.direction}
      </p>
      {criterion.content.examples.length > 0 ? (
        <p className="text-gray-600 text-xs">
          {criterion.content.examples.length} exemple
          {criterion.content.examples.length > 1 ? 's' : ''} dans la version jouée
        </p>
      ) : null}
      <Link
        to="/admin/criteres/$criterionId"
        params={{ criterionId: criterion.id }}
        search={{ version: criterion.versionId }}
        className="text-bleu-nuit text-sm underline underline-offset-2"
      >
        Ouvrir la version jouée
      </Link>
    </div>
  );
}
